import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Users, BarChart3, LogOut, User, ShoppingBag, Menu, X, Home, Building2 } from 'lucide-react';
import ProfileEditor from './ProfileEditor';
import LeadManager from './LeadManager';
import Analytics from './Analytics';
import Orders from './Orders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [corpRole, setCorpRole] = useState(null);

  const fetchUserAndProfile = useCallback(async () => {
    try {
      const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(userResponse.data);

      try {
        const profileResponse = await axios.get(`${BACKEND_URL}/api/profiles/me`, {
          withCredentials: true
        });
        setProfile(profileResponse.data);
      } catch (err) {
        console.log('No profile yet');
      }

      try {
        const corpResp = await axios.get(`${BACKEND_URL}/api/corporate/me`, { withCredentials: true });
        setCorpRole(corpResp.data.role);
      } catch (_err) {
        setCorpRole(null);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserAndProfile();
  }, [fetchUserAndProfile]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA]">
        <div className="w-16 h-16 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="dashboard-container">
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur-xl border-b border-[#0A0A0A]/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-heading font-bold text-[#0A0A0A]">Sol Digital</h1>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-[#0A0A0A] p-2" data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div className="flex pt-16 lg:pt-0">
        <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#0A0A0A]/10 p-6 transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} lg:min-h-screen`}>
          <div className="mb-8 hidden lg:block">
            <h1 className="text-2xl font-heading font-bold text-[#0A0A0A]">Sol Digital</h1>
            <p className="text-sm text-[#525252] mt-1">Digital Business Cards</p>
          </div>

          <nav className="space-y-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-[#525252] hover:text-[#0A0A0A] hover:bg-[#F5F1EA]"
              data-testid="nav-home-link"
            >
              <Home className="w-5 h-5" />
              <span>Home</span>
            </Link>
            <div className="h-px bg-[#F5F1EA] my-2"></div>
            <Link
              to="/dashboard"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/dashboard') && location.pathname === '/dashboard' ? 'bg-[#B8935A]/15 text-[#B8935A]' : 'text-[#0A0A0A] hover:bg-[#F5F1EA]'}`}
              data-testid="nav-editor-link"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Editor</span>
            </Link>
            <Link
              to="/dashboard/leads"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/dashboard/leads') ? 'bg-[#B8935A]/15 text-[#B8935A]' : 'text-[#0A0A0A] hover:bg-[#F5F1EA]'}`}
              data-testid="nav-leads-link"
            >
              <Users className="w-5 h-5" />
              <span>Leads</span>
            </Link>
            <Link
              to="/dashboard/analytics"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/dashboard/analytics') ? 'bg-[#B8935A]/15 text-[#B8935A]' : 'text-[#0A0A0A] hover:bg-[#F5F1EA]'}`}
              data-testid="nav-analytics-link"
            >
              <BarChart3 className="w-5 h-5" />
              <span>Analytics</span>
            </Link>
            <Link
              to="/dashboard/orders"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive('/dashboard/orders') ? 'bg-[#B8935A]/15 text-[#B8935A]' : 'text-[#0A0A0A] hover:bg-[#F5F1EA]'}`}
              data-testid="nav-orders-link"
            >
              <ShoppingBag className="w-5 h-5" />
              <span>Orders</span>
            </Link>
            {corpRole === 'admin' && (
              <Link
                to="/corporate/dashboard"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-[#B8935A] hover:bg-[#B8935A]/10 border border-[#B8935A]/30 mt-2"
                data-testid="nav-corporate-link"
              >
                <Building2 className="w-5 h-5" />
                <span>Corporate</span>
              </Link>
            )}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#B8935A]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#B8935A]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0A0A0A] truncate">{user?.name}</p>
                <p className="text-xs text-[#525252] truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-[#525252] hover:text-[#0A0A0A] hover:bg-[#F5F1EA]"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay"></div>
        )}

        <main className="flex-1 p-4 lg:p-6 w-full lg:w-auto overflow-x-hidden">
          <Routes>
            <Route index element={<ProfileEditor profile={profile} onProfileUpdate={fetchUserAndProfile} />} />
            <Route path="leads" element={<LeadManager />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="orders" element={<Orders />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
